import {
  faArrowLeft,
  faArrowRight,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import Book from 'models/Book';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import ReadingUtils from 'utils/ReadingUtils';
import Button from 'view/components/buttons/Button';
import ReadingFrame from 'view/components/common/ReadingFrame';
import parse from 'html-react-parser';
import MissedWordDisplay from '../../MissedWordDisplay';
import MissedWord from 'models/MissedWord';
import triggerConfetti from 'contexts/celebrations/Confetti';
import AssignmentFeedback from 'models/assignments/AssignmentFeedback';
import StudentQuestion from 'models/StudentQuestion';
import QuestionResponse from 'models/QuestionResponse';
import Question from 'models/Question';
import MultipartAssignment from '../multipart/Multipart';

import 'view/style/teacher/components/grading/teacherGradingView.css';

interface WritingAssignmentProps {
  assignment: StudentGradebookEntry;
  book?: Book;
  teacherView?: boolean;
  missedWords?: MissedWord[];
  questions: StudentQuestion[];
  onCompletion: () => void;
  onMissedWord: (word: string, index: number, known: boolean) => void;
  onAddFeedback: (feedback: AssignmentFeedback) => void;
  onUpdateQuestionResponse: (
    question: Question,
    response: QuestionResponse,
  ) => void;
  setActiveQuestion: (question: StudentQuestion) => void;
}

export enum WritingType {
  FREEFORM,
  COMPREHENSION,
}

type ExpandWord = {
  word: string;
  index: number;
};

interface HtmlContent {
  content: string;
  index: number;
}

const WritingAssignment: React.FC<WritingAssignmentProps> = ({
  assignment,
  book,
  teacherView = false,
  missedWords,
  questions,
  onMissedWord,
  onCompletion,
  onUpdateQuestionResponse,
  onAddFeedback,
  setActiveQuestion,
}) => {
  const [showBook, setShowBook] = useState<boolean>(false);
  const [words, setWords] = useState<string[]>([]);
  const [expandWord, setExpandWord] = useState<ExpandWord | null>();
  const [htmlContent, setHtmlContent] = useState<HtmlContent>({
    content: '',
    index: 0,
  });

  // Book content setup
  useEffect(() => {
    if (!book || !book.html_content || words.length > 0) return;
    const initialHtmlContent = book.html_content.replaceAll('&nbsp;', ' ');
    setHtmlContent({
      content: initialHtmlContent,
      index: ReadingUtils.getFirstWordIndexFromHTMLString(initialHtmlContent),
    });
    setWords(() => book.getWords());
  }, [book, words.length]);

  // Span generation for interactive words
  const generateSpanElement = useCallback(
    (wordIndex: number) => {
      if (wordIndex >= words.length) return;
      const openTag = `<span data-id='${wordIndex}' key='word-${wordIndex}'>`;
      const closeTag = '</span>';

      setHtmlContent((prevContent: HtmlContent) => {
        const nextHtmlWordIdx =
          prevContent.index +
          ReadingUtils.getFirstWordIndexFromHTMLString(
            prevContent.content.slice(prevContent.index),
          );
        const nextHtmlWordEndIdx = nextHtmlWordIdx + words[wordIndex].length;

        const newHtmlIdx =
          nextHtmlWordEndIdx + openTag.length + closeTag.length;

        let newContent = `${prevContent.content.slice(
          0,
          nextHtmlWordIdx,
        )}${openTag}${prevContent.content.slice(
          nextHtmlWordIdx,
          nextHtmlWordEndIdx,
        )}${closeTag}`;

        newContent += `${prevContent.content.slice(nextHtmlWordEndIdx)}`;

        return {
          content: newContent,
          index: newHtmlIdx,
        };
      });
    },
    [words],
  );

  // Process all words
  useEffect(() => {
    if (!words) return;
    for (let i = 0; i < words.length; i += 1) {
      generateSpanElement(i);
    }
  }, [words, generateSpanElement]);

  // Handle span click for reading mode
  const handleSpanClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'SPAN' && target.dataset.id) {
      const idx = parseInt(target.dataset.id);
      setExpandWord({ word: words[idx], index: idx });
    }
  };

  // Handle completion button click
  const handleAssignmentCompleted = () => {
    if (teacherView) return;
    // Save any unsaved changes before completing

    // Call completion callback
    if (onCompletion) onCompletion();
    triggerConfetti(3000);
  };

  // Determine writing type based on book presence
  const writingType = useMemo(
    () => (!!book ? WritingType.COMPREHENSION : WritingType.FREEFORM),
    [book],
  );

  // Set book visibility based on type
  useEffect(() => {
    setShowBook(writingType === WritingType.COMPREHENSION);
  }, [writingType]);

  return (
    <>
      {showBook ? (
        <>
          {expandWord && !teacherView ? (
            <MissedWordDisplay
              word={expandWord.word}
              index={expandWord.index}
              onExit={() => setExpandWord(null)}
              onMissedWord={onMissedWord}
              showPrompt={
                missedWords?.findIndex((mw) => mw.word === expandWord.word) ===
                -1
              }
              showMissedWordIndicator={false}
            />
          ) : (
            <ReadingFrame
              isInteractive={!teacherView}
              interactionCallback={handleSpanClick}
            >
              {parse(htmlContent.content)}
            </ReadingFrame>
          )}
        </>
      ) : (
        <MultipartAssignment
          questions={questions}
          teacherView={teacherView}
          showFeedback={false}
          allowMultipleAttempts={false}
          onCompletion={onCompletion}
          onUpdateResponse={onUpdateQuestionResponse}
          onSubmit={() => {}}
          setActiveQuestion={setActiveQuestion}
          onAddFeedback={onAddFeedback}
        />
      )}
      <div className="assignment-editor-actions">
        {showBook ? (
          <Button
            type="go"
            onClick={() => setShowBook(false)}
            label="To writing"
            text="To writing"
            fontAwesomeIcon={faArrowRight}
          />
        ) : (
          <>
            {writingType === WritingType.COMPREHENSION && (
              <Button
                type="edit"
                onClick={() => setShowBook(true)}
                label="To reading"
                fontAwesomeIcon={faArrowLeft}
                text="To reading"
              />
            )}
            {!teacherView &&
              !assignment.submission.date_completed &&
              assignment.submission.date_completed !== '' && (
                <Button
                  type="go"
                  onClick={handleAssignmentCompleted}
                  label="Submit"
                  fontAwesomeIcon={faSave}
                  text="Submit"
                />
              )}
          </>
        )}
      </div>
    </>
  );
};

export default WritingAssignment;
